/**
 * File Parser Utility
 * Abstraction layer to extract plain text from various file formats.
 * Supports: .txt, .md (native), .docx (direct XML parse), .pdf (pdfjs-dist)
 * Runs entirely client-side - no server needed.
 * 
 * DOCX: Tu parse XML truc tiep (khong dung mammoth) de dam bao
 * roundtrip 100% chinh xac khi export.
 * 
 * QUAN TRONG: Dung DOMParser de parse XML chinh xac,
 * khong dung regex de tim <w:p> (regex fails voi nested structures).
 */

import JSZip from 'jszip';

/** Mapping tu 1 paragraph trong XML -> vi tri trong sourceText */
export interface DocxParagraphMapping {
    /** Index cua paragraph trong danh sach non-empty, non-fallback paragraphs */
    index: number;
    /** Text content cua paragraph (decoded, chi text thuan) */
    text: string;
    /** Vi tri bat dau trong XML string (cua toan bo <w:p>...</w:p>) */
    xmlStart: number;
    /** Vi tri ket thuc trong XML string */
    xmlEnd: number;
    /** Co phai nam trong mc:Fallback khong */
    isFallback: boolean;
}

/** Mapping tu 1 text run trong PPTX slide -> vi tri trong sourceText */
export interface PptxSlideMapping {
    /** Slide number (1-based) */
    slideNumber: number;
    /** Slide XML file name (e.g. "ppt/slides/slide1.xml") */
    slidePath: string;
    /** Paragraphs in this slide with their text and XML positions */
    paragraphs: PptxParagraphMapping[];
}

export interface PptxParagraphMapping {
    /** Text content of the paragraph */
    text: string;
    /** Start position in XML string */
    xmlStart: number;
    /** End position in XML string */
    xmlEnd: number;
    /** Shape name/id containing this paragraph (for debugging) */
    shapeName?: string;
}

export interface ParseResult {
    text: string;
    originalFormat: 'txt' | 'md' | 'docx' | 'pdf' | 'pptx';
    pageCount?: number;       // For PDF or PPTX (slide count)
    warnings?: string[];      // E.g. "PDF scan", "DOCX empty"
    arrayBuffer?: ArrayBuffer; // Original file bytes (DOCX/PPTX, for export)
    /** Paragraph mapping cho DOCX - dung de export chinh xac 100% */
    docxParagraphMap?: DocxParagraphMapping[];
    /** Slide mapping cho PPTX - dung de export chinh xac */
    pptxSlideMappings?: PptxSlideMapping[];
}

/** All file extensions we accept */
// PDF + TXT tạm thời ẩn — parser logic vẫn còn ở dưới, bật lại bằng cách thêm vào 2 mảng dưới.
const SUPPORTED_EXTENSIONS = ['.md', '.docx', '.pptx'];

/** For file input accept attribute */
export const ACCEPTED_FILE_TYPES = '.md,.docx,.pptx';

/**
 * Check if a file name has a supported extension.
 */
export function isSupportedFile(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return SUPPORTED_EXTENSIONS.some(ext => lower.endsWith(ext));
}

/**
 * Get file extension (lowercase, with dot).
 */
export function getFileExtension(fileName: string): string {
    const idx = fileName.lastIndexOf('.');
    if (idx === -1) return '';
    return fileName.slice(idx).toLowerCase();
}

/**
 * Get a short format label for UI display.
 */
export function getFormatLabel(fileName: string): string {
    const ext = getFileExtension(fileName);
    switch (ext) {
        case '.docx': return 'DOCX';
        case '.pptx': return 'PPTX';
        case '.pdf': return 'PDF';
        case '.md': return 'MD';
        case '.txt': return 'TXT';
        default: return ext.replace('.', '').toUpperCase();
    }
}

/**
 * Parse a file into plain text.
 * This is the main entry point - handles all supported formats.
 * Uses dynamic imports for pdfjs-dist to avoid loading
 * heavy dependencies at module init time.
 */
export async function parseFile(file: File): Promise<ParseResult> {
    const ext = getFileExtension(file.name);

    switch (ext) {
        case '.txt':
            return { text: await file.text(), originalFormat: 'txt' };

        case '.md':
            return { text: await file.text(), originalFormat: 'md' };

        case '.docx':
            return parseDocxDirect(file);

        case '.pptx':
            return parsePptx(file);

        case '.pdf':
            return parsePdf(file);

        default:
            throw new Error(`Dinh dang khong ho tro: ${ext}`);
    }
}

// ============================================
// DOCX XML HELPERS
// ============================================

/**
 * Tim tat ca cac range cua <mc:Fallback>...</mc:Fallback> trong XML string.
 * Dung de xac dinh paragraphs nao la fallback (duplicates).
 */
function findFallbackRanges(xml: string): Array<{ start: number; end: number }> {
    const ranges: Array<{ start: number; end: number }> = [];
    const regex = /<mc:Fallback[^>]*>/g;
    let match;

    while ((match = regex.exec(xml)) !== null) {
        const startPos = match.index;
        const closeTag = '</mc:Fallback>';
        const closeIdx = xml.indexOf(closeTag, startPos);
        if (closeIdx !== -1) {
            ranges.push({ start: startPos, end: closeIdx + closeTag.length });
        }
    }
    return ranges;
}

/**
 * Kiem tra 1 position co nam trong bat ky Fallback range nao khong.
 */
function isInFallbackRange(pos: number, fallbackRanges: Array<{ start: number; end: number }>): boolean {
    for (const range of fallbackRanges) {
        if (pos >= range.start && pos < range.end) return true;
    }
    return false;
}

/**
 * Kiem tra node co ancestor voi tagName nao do giua no va stopAt element.
 * Dung de xac dinh <w:t> co nam trong nested <w:p> hoac <mc:Fallback> khong.
 */
function hasAncestorBetween(node: Node, stopAt: Element, tagName: string): boolean {
    let parent = node.parentElement;
    while (parent && parent !== stopAt) {
        if (parent.tagName === tagName || parent.localName === tagName) return true;
        parent = parent.parentElement;
    }
    return false;
}

/**
 * Kiem tra element co nam trong <mc:Fallback> ancestor khong.
 * Dung DOM ancestry - chinh xac hon string position check.
 */
function isInsideFallback(element: Element): boolean {
    let parent = element.parentElement;
    while (parent) {
        if (parent.tagName === 'mc:Fallback' || parent.localName === 'Fallback') return true;
        parent = parent.parentElement;
    }
    return false;
}

/**
 * Extract plain text tu 1 DOM Element cua <w:p> (khong co format markers).
 *
 * QUAN TRONG: Chi lay <w:t> tags thuoc TRUC TIEP paragraph nay:
 * - KHONG lay text tu nested <w:p> (text boxes, table cells)
 * - KHONG lay text tu <mc:Fallback> (duplicate content)
 * - KHONG lay text tu <w:del> (deleted content in track changes)
 *
 * Bold/italic markers bi BO do training/benchmark khong can preserve format.
 */
function extractTextFromElement(pElement: Element): string {
    let result = '';
    const rNodes = pElement.getElementsByTagName('w:r');

    for (let ri = 0; ri < rNodes.length; ri++) {
        const rNode = rNodes[ri];
        if (hasAncestorBetween(rNode, pElement, 'w:p')) continue;
        if (hasAncestorBetween(rNode, pElement, 'mc:Fallback')) continue;
        if (hasAncestorBetween(rNode, pElement, 'w:del')) continue;
        if (rNode.getElementsByTagName('w:drawing').length > 0) continue;
        if (rNode.getElementsByTagName('w:pict').length > 0) continue;
        if (rNode.getElementsByTagName('w:object').length > 0) continue;

        const tNodes = rNode.getElementsByTagName('w:t');
        for (let ti = 0; ti < tNodes.length; ti++) {
            const tNode = tNodes[ti];
            if (tNode.parentElement !== rNode) continue;
            const tc = tNode.textContent;
            if (tc) result += tc;
        }
    }

    return result;
}

// ============================================
// PARSE DOCX - Tu parse XML (thay mammoth)
// ============================================

/**
 * Parse DOCX truc tiep tu XML bang DOMParser.
 * 
 * Uu diem so voi mammoth + regex:
 * - Parse XML bang DOM -> chinh xac 100% voi nested structures
 * - Doc theo DUNG thu tu XML paragraphs
 * - Tao paragraphMap cho export chinh xac 100%
 * - Khong phu thuoc thu vien ben ngoai (chi JSZip + DOMParser co san)
 * 
 * Logic:
 * 1. JSZip mo file DOCX
 * 2. Doc word/document.xml
 * 3. Tim tat ca Fallback ranges (dung string search)
 * 4. Tim tat ca <w:p> positions trong XML string (cho xmlStart/xmlEnd)
 * 5. Parse XML bang DOMParser -> lay DOM tree
 * 6. Duyet <w:p> elements bang DOM, extract text tu <w:t>
 * 7. Map DOM paragraphs voi string positions
 * 8. Skip Fallback paragraphs va empty paragraphs
 * 9. Join text thanh sourceText, tao paragraphMap
 */
async function parseDocxDirect(file: File): Promise<ParseResult> {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    const docXmlFile = zip.file('word/document.xml');
    if (!docXmlFile) {
        throw new Error('File DOCX khong hop le: thieu word/document.xml');
    }

    const docXml = await docXmlFile.async('string');
    const warnings: string[] = [];

    // 1. Tim tat ca Fallback ranges trong RAW XML string
    const fallbackRanges = findFallbackRanges(docXml);

    // 2. Tim tat ca vi tri <w:p trong XML string (cho xmlStart/xmlEnd)
    //    Dung regex chi de tim POSITIONS, khong de extract content
    const paraPositions: Array<{ xmlStart: number; xmlEnd: number }> = [];
    const posRegex = /<w:p[ >]/g;
    let posMatch;

    // Helper: kiem tra 1 <w:p ... co phai self-closing (ket thuc />) khong
    function isSelfClosingWp(startPos: number): boolean {
        // Tim ky tu > dau tien sau <w:p
        const closeAngle = docXml.indexOf('>', startPos + 3);
        if (closeAngle === -1) return false;
        // Neu ky tu truoc > la / -> self-closing <w:p ... />
        return docXml[closeAngle - 1] === '/';
    }

    while ((posMatch = posRegex.exec(docXml)) !== null) {
        const xmlStart = posMatch.index;

        // Ban dau skip self-closing <w:p .../> (empty paragraphs), NHUNG
        // DOMParser.getElementsByTagName('w:p') se tra ve CA NHUNG THE NAY!
        // De mang paraPositions va DOM paragraphs KHOP INDEX 1:1,
        // chung ta KHONG DUOC BO QUA. Phai dua vao danh sach luon.
        if (isSelfClosingWp(xmlStart)) {
            const closeAngle = docXml.indexOf('>', xmlStart + 3);
            const xmlEnd = closeAngle !== -1 ? closeAngle + 1 : xmlStart + 4;
            paraPositions.push({ xmlStart, xmlEnd });
            continue;
        }

        // Tim closing </w:p> tag phu hop
        // Can xu ly nested <w:p> (trong tables)
        let depth = 1;
        let searchPos = xmlStart + posMatch[0].length;

        while (depth > 0 && searchPos < docXml.length) {
            const nextOpen = docXml.indexOf('<w:p ', searchPos);
            const nextOpenNoAttr = docXml.indexOf('<w:p>', searchPos);
            const nextClose = docXml.indexOf('</w:p>', searchPos);

            // Lay vi tri open tag gan nhat (co the la <w:p > hoac <w:p>)
            let nextOpenPos = -1;
            if (nextOpen !== -1 && nextOpenNoAttr !== -1) {
                nextOpenPos = Math.min(nextOpen, nextOpenNoAttr);
            } else if (nextOpen !== -1) {
                nextOpenPos = nextOpen;
            } else {
                nextOpenPos = nextOpenNoAttr;
            }

            if (nextClose === -1) break; // Khong tim thay closing tag -> thoat

            if (nextOpenPos !== -1 && nextOpenPos < nextClose) {
                // Co 1 nested <w:p> truoc </w:p>
                // Nhung can kiem tra xem no co phai self-closing khong
                if (isSelfClosingWp(nextOpenPos)) {
                    // Self-closing nested <w:p.../> -> SKIP (khong tang depth)
                    const skipEnd = docXml.indexOf('>', nextOpenPos) + 1;
                    searchPos = skipEnd > 0 ? skipEnd : nextOpenPos + 4;
                } else {
                    depth++;
                    searchPos = nextOpenPos + 4; // Skip qua "<w:p"
                }
            } else {
                // </w:p> gap truoc -> giam depth
                depth--;
                if (depth === 0) {
                    const xmlEnd = nextClose + '</w:p>'.length;
                    paraPositions.push({ xmlStart, xmlEnd });
                }
                searchPos = nextClose + '</w:p>'.length;
            }
        }
    }

    // 3. Parse XML bang DOMParser de lay DOM tree chinh xac
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXml, 'application/xml');

    // Lay tat ca <w:p> elements tu DOM
    const allParagraphs = xmlDoc.getElementsByTagName('w:p');

    // 4. Map DOM paragraphs voi string positions
    //    DOM tree va positions deu duyet theo document order -> 1:1
    const paragraphMap: DocxParagraphMapping[] = [];
    const textLines: string[] = [];
    let paraIndex = 0;

    // So luong paragraphs tu DOM va tu position search co the khac nhau
    // (do self-closing <w:p/> hoac nested), nen ta dung MIN
    const count = Math.min(allParagraphs.length, paraPositions.length);

    for (let i = 0; i < count; i++) {
        const pElement = allParagraphs[i];
        const pos = paraPositions[i];

        // Check neu paragraph nam trong Fallback - dung DOM ancestry (chinh xac hon string position)
        const isFallback = isInsideFallback(pElement);

        // Extract text tu DOM element
        // extractTextFromElement chi lay <w:t> TRUC TIEP cua paragraph nay
        // KHONG lay text tu nested <w:p> (text boxes) hoac <mc:Fallback>
        const text = extractTextFromElement(pElement);

        if (text.trim() && !isFallback) {
            paragraphMap.push({
                index: paraIndex,
                text: text,
                xmlStart: pos.xmlStart,
                xmlEnd: pos.xmlEnd,
                isFallback: false,
            });
            textLines.push(text);
            paraIndex++;
        } else if (text.trim() && isFallback) {
            paragraphMap.push({
                index: -1,
                text: text,
                xmlStart: pos.xmlStart,
                xmlEnd: pos.xmlEnd,
                isFallback: true,
            });
        }
        // Empty paragraphs: bo qua hoan toan
    }

    // 5. Join thanh sourceText (1 dong = 1 paragraph)
    const sourceText = textLines.join('\n');

    if (!sourceText.trim()) {
        warnings.push('File DOCX rong hoac khong co noi dung text');
    }

    const activeParagraphs = paragraphMap.filter(p => !p.isFallback);
    console.log(`[parseDocxDirect] Parsed ${activeParagraphs.length} paragraphs (${paragraphMap.filter(p => p.isFallback).length} fallback skipped)`);

    // Also extract chart labels (word/charts/chart*.xml)
    const chartFiles = Object.keys(zip.files)
        .filter(name => /^word\/charts\/chart\d+\.xml$/.test(name))
        .sort();

    let chartText = '';
    for (const chartPath of chartFiles) {
        const chartXml = await zip.file(chartPath)!.async('string');
        const chartTexts: string[] = [];

        // Extract <c:v> text labels (skip numbers and generic headers)
        const cvRegex = /<c:v>([^<]*)<\/c:v>/g;
        let cvMatch;
        while ((cvMatch = cvRegex.exec(chartXml)) !== null) {
            const val = cvMatch[1].trim();
            if (val && isNaN(Number(val)) && val !== 'Column1') {
                chartTexts.push(val);
            }
        }

        // Extract <a:t> chart titles/labels
        const atRegex = /<a:t(?:\s[^>]*)?>([^<]*)<\/a:t>/g;
        let atMatch;
        while ((atMatch = atRegex.exec(chartXml)) !== null) {
            const val = atMatch[1].trim();
            if (val) chartTexts.push(val);
        }

        if (chartTexts.length > 0) {
            chartText += (chartText ? '\n' : '') + chartTexts.join('\n');
        }
    }

    // Append chart text to sourceText
    const fullText = chartText ? sourceText + '\n' + chartText : sourceText;

    if (chartFiles.length > 0) {
        console.log(`[parseDocxDirect] Extracted text from ${chartFiles.length} charts`);
    }

    return {
        text: fullText,
        originalFormat: 'docx',
        warnings: warnings.length > 0 ? warnings : undefined,
        arrayBuffer,
        docxParagraphMap: paragraphMap,
    };
}

/**
 * Regenerate paragraphMap from a DOCX ArrayBuffer.
 * Used as a fallback when paragraphMap is missing (e.g., loaded from disk before persist fix).
 */
export async function regenerateParagraphMap(buffer: ArrayBuffer): Promise<DocxParagraphMapping[]> {
    const zip = await JSZip.loadAsync(buffer);
    const docXmlFile = zip.file('word/document.xml');
    if (!docXmlFile) return [];

    const docXml = await docXmlFile.async('string');
    const fallbackRanges = findFallbackRanges(docXml);

    const paraPositions: Array<{ xmlStart: number; xmlEnd: number }> = [];
    const posRegex = /<w:p[ >]/g;
    let posMatch;

    function isSelfClosingWp(startPos: number): boolean {
        const closeAngle = docXml.indexOf('>', startPos + 3);
        if (closeAngle === -1) return false;
        return docXml[closeAngle - 1] === '/';
    }

    while ((posMatch = posRegex.exec(docXml)) !== null) {
        const xmlStart = posMatch.index;
        if (isSelfClosingWp(xmlStart)) {
            const closeAngle = docXml.indexOf('>', xmlStart + 3);
            const xmlEnd = closeAngle !== -1 ? closeAngle + 1 : xmlStart + 4;
            paraPositions.push({ xmlStart, xmlEnd });
            continue;
        }
        let depth = 1;
        let searchPos = xmlStart + posMatch[0].length;
        while (depth > 0 && searchPos < docXml.length) {
            const nextOpen = docXml.indexOf('<w:p ', searchPos);
            const nextOpenNoAttr = docXml.indexOf('<w:p>', searchPos);
            const nextClose = docXml.indexOf('</w:p>', searchPos);
            let nextOpenPos = -1;
            if (nextOpen !== -1 && nextOpenNoAttr !== -1) nextOpenPos = Math.min(nextOpen, nextOpenNoAttr);
            else if (nextOpen !== -1) nextOpenPos = nextOpen;
            else nextOpenPos = nextOpenNoAttr;
            if (nextClose === -1) break;
            if (nextOpenPos !== -1 && nextOpenPos < nextClose) {
                if (isSelfClosingWp(nextOpenPos)) {
                    const skipEnd = docXml.indexOf('>', nextOpenPos) + 1;
                    searchPos = skipEnd > 0 ? skipEnd : nextOpenPos + 4;
                } else {
                    depth++;
                    searchPos = nextOpenPos + 4;
                }
            } else {
                depth--;
                if (depth === 0) {
                    paraPositions.push({ xmlStart, xmlEnd: nextClose + '</w:p>'.length });
                }
                searchPos = nextClose + '</w:p>'.length;
            }
        }
    }

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXml, 'application/xml');
    const allParagraphs = xmlDoc.getElementsByTagName('w:p');
    const paragraphMap: DocxParagraphMapping[] = [];
    let paraIndex = 0;
    const count = Math.min(allParagraphs.length, paraPositions.length);

    for (let i = 0; i < count; i++) {
        const pElement = allParagraphs[i];
        const pos = paraPositions[i];
        const isFallback = isInsideFallback(pElement);
        const text = extractTextFromElement(pElement);

        if (text.trim() && !isFallback) {
            paragraphMap.push({ index: paraIndex, text, xmlStart: pos.xmlStart, xmlEnd: pos.xmlEnd, isFallback: false });
            paraIndex++;
        } else if (text.trim() && isFallback) {
            paragraphMap.push({ index: -1, text, xmlStart: pos.xmlStart, xmlEnd: pos.xmlEnd, isFallback: true });
        }
    }

    console.log(`[regenerateParagraphMap] Regenerated ${paragraphMap.filter(p => !p.isFallback).length} active paragraphs`);
    return paragraphMap;
}

/**
 * Extract text from a PDF file using pdfjs-dist.
 * Extracts text layer only - scanned PDFs (images) will return empty text.
 */
async function parsePdf(file: File): Promise<ParseResult> {
    const pdfjsLib = await import('pdfjs-dist');

    // Use local worker via Vite import instead of CDN
    // @ts-ignore - Vite ?url import
    const workerUrl = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl.default;

    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const textParts: string[] = [];
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();

        // Smart text joining: use item positions to determine spacing
        let pageText = '';
        let lastX = -1;
        let lastWidth = 0;
        let lastY = -1;
        const items = content.items.filter((item: any) => 'str' in item) as any[];

        for (const item of items) {
            const str = item.str as string;
            if (!str) continue;

            const [, , , , x, y] = item.transform || [0, 0, 0, 0, 0, 0];

            if (lastY !== -1) {
                const yDiff = Math.abs(y - lastY);
                if (yDiff > 5) {
                    pageText += '\n';
                    lastX = -1;
                    lastWidth = 0;
                }
            }

            if (lastX !== -1 && lastWidth > 0) {
                const gap = x - (lastX + lastWidth);
                const fontSize = Math.abs(item.transform?.[0] || 12);
                if (gap > fontSize * 0.3) {
                    pageText += ' ';
                }
            }

            pageText += str;
            lastX = x;
            lastWidth = item.width || 0;
            lastY = y;

            if (item.hasEOL) {
                pageText += '\n';
                lastX = -1;
                lastWidth = 0;
            }
        }

        if (pageText.trim()) {
            textParts.push(pageText.trim());
        }
    }

    const fullText = textParts.join('\n\n');
    const warnings: string[] = [];

    if (!fullText.trim()) {
        warnings.push('PDF nay co the la file scan (hinh anh). Khong extract duoc text.');
    }

    return {
        text: fullText,
        originalFormat: 'pdf',
        pageCount: pdf.numPages,
        warnings: warnings.length > 0 ? warnings : undefined,
    };
}

// ============================================
// PPTX PARSER (DOMParser-based)
// ============================================

/**
 * Extract text from a PPTX file.
 *
 * Covers all text containers per Microsoft OpenXML spec:
 * 1. Slides (ppt/slides/slide*.xml) - shapes, tables, groups
 * 2. Diagrams/SmartArt (ppt/diagrams/data*.xml)
 *
 * Uses DOMParser (same approach as DOCX parser) for correct handling
 * of nested XML structures like tables within groups within shapes.
 */
async function parsePptx(file: File): Promise<ParseResult> {
    const arrayBuffer = await file.arrayBuffer();
    const zip = await JSZip.loadAsync(arrayBuffer);

    // Find all slide XML files, sorted by slide number
    const slideFiles = Object.keys(zip.files)
        .filter(name => /^ppt\/slides\/slide\d+\.xml$/.test(name))
        .sort((a, b) => {
            const numA = parseInt(a.match(/slide(\d+)/)?.[1] || '0');
            const numB = parseInt(b.match(/slide(\d+)/)?.[1] || '0');
            return numA - numB;
        });

    if (slideFiles.length === 0) {
        return {
            text: '',
            originalFormat: 'pptx',
            pageCount: 0,
            warnings: ['PPTX khong co slide nao.'],
            arrayBuffer,
        };
    }

    const textParts: string[] = [];
    const slideMappings: PptxSlideMapping[] = [];

    for (const slidePath of slideFiles) {
        const slideXml = await zip.file(slidePath)!.async('string');
        const slideNum = parseInt(slidePath.match(/slide(\d+)/)?.[1] || '0');

        const { text: slideText, paragraphs } = extractPptxSlideText(slideXml);

        if (slideText.trim()) {
            textParts.push(slideText);
        }

        slideMappings.push({
            slideNumber: slideNum,
            slidePath,
            paragraphs,
        });
    }

    // Also extract SmartArt/diagram text (ppt/diagrams/data*.xml)
    const diagramFiles = Object.keys(zip.files)
        .filter(name => /^ppt\/diagrams\/data\d+\.xml$/.test(name))
        .sort();

    for (const diagPath of diagramFiles) {
        const diagXml = await zip.file(diagPath)!.async('string');
        const { text: diagText } = extractPptxSlideText(diagXml);
        if (diagText.trim()) {
            textParts.push(diagText);
        }
    }

    // Also extract chart labels (ppt/charts/chart*.xml)
    // Chart labels are in <c:v> tags — only extract text labels, not numeric values
    const chartFiles = Object.keys(zip.files)
        .filter(name => /^ppt\/charts\/chart\d+\.xml$/.test(name))
        .sort();

    for (const chartPath of chartFiles) {
        const chartXml = await zip.file(chartPath)!.async('string');
        const chartTexts: string[] = [];
        const cvRegex = /<c:v>([^<]*)<\/c:v>/g;
        let cvMatch;
        while ((cvMatch = cvRegex.exec(chartXml)) !== null) {
            const val = cvMatch[1].trim();
            // Only extract text labels, skip numbers and generic headers
            if (val && isNaN(Number(val)) && val !== 'Column1') {
                chartTexts.push(val);
            }
        }
        if (chartTexts.length > 0) {
            textParts.push(chartTexts.join('\n'));
        }
    }

    const fullText = textParts.join('\n');
    const warnings: string[] = [];

    if (!fullText.trim()) {
        warnings.push('PPTX nay khong co text. Co the chi co hinh anh.');
    }

    return {
        text: fullText,
        originalFormat: 'pptx',
        pageCount: slideFiles.length,
        warnings: warnings.length > 0 ? warnings : undefined,
        arrayBuffer,
        pptxSlideMappings: slideMappings,
    };
}

/**
 * Extract text from a PPTX slide/diagram XML using DOMParser.
 *
 * Strategy (per Microsoft OpenXML docs):
 * - Parse XML into DOM tree
 * - Find ALL <a:p> paragraphs (covers shapes, tables, groups, any depth)
 * - For each <a:p>, concatenate all <a:t> text nodes (runs with different formatting)
 * - Skip empty paragraphs
 * - Track XML string positions for export (replace text roundtrip)
 *
 * Handles: p:sp, p:grpSp (nested), p:graphicFrame (tables), a:tbl/a:tc, any depth.
 */
function extractPptxSlideText(slideXml: string): {
    text: string;
    paragraphs: PptxParagraphMapping[];
} {
    const paragraphs: PptxParagraphMapping[] = [];
    const textLines: string[] = [];

    // 1. Build string position index for all <a:p> tags (for export mapping)
    //    Same approach as DOCX parser: find positions in raw string, then match with DOM
    const paraPositions: Array<{ xmlStart: number; xmlEnd: number }> = [];
    {
        // Use depth tracking to correctly match <a:p> open/close (handles nesting)
        let searchPos = 0;
        while (searchPos < slideXml.length) {
            const openIdx = slideXml.indexOf('<a:p', searchPos);
            if (openIdx === -1) break;

            // Check it's actually <a:p> or <a:p ...> (not <a:pPr> etc.)
            const afterTag = slideXml[openIdx + 4]; // char after "<a:p"
            if (afterTag !== '>' && afterTag !== ' ' && afterTag !== '/') {
                searchPos = openIdx + 4;
                continue;
            }

            // Self-closing <a:p/>
            const tagEnd = slideXml.indexOf('>', openIdx);
            if (tagEnd === -1) break;
            if (slideXml[tagEnd - 1] === '/') {
                paraPositions.push({ xmlStart: openIdx, xmlEnd: tagEnd + 1 });
                searchPos = tagEnd + 1;
                continue;
            }

            // Find matching </a:p> with depth tracking (handles nested <a:p> inside tables etc.)
            let depth = 1;
            let pos = tagEnd + 1;
            while (pos < slideXml.length && depth > 0) {
                const nextOpen = slideXml.indexOf('<a:p', pos);
                const nextClose = slideXml.indexOf('</a:p>', pos);

                if (nextClose === -1) break;

                // Check if nextOpen is actually <a:p> (not <a:pPr>)
                let isRealOpen = false;
                if (nextOpen !== -1 && nextOpen < nextClose) {
                    const ch = slideXml[nextOpen + 4];
                    isRealOpen = (ch === '>' || ch === ' ' || ch === '/');
                }

                if (isRealOpen && nextOpen < nextClose) {
                    depth++;
                    pos = slideXml.indexOf('>', nextOpen) + 1;
                } else {
                    depth--;
                    if (depth === 0) {
                        const endPos = nextClose + '</a:p>'.length;
                        paraPositions.push({ xmlStart: openIdx, xmlEnd: endPos });
                        searchPos = endPos;
                    }
                    pos = nextClose + '</a:p>'.length;
                }
            }

            if (depth > 0) {
                // Unmatched tag, skip
                searchPos = tagEnd + 1;
            }
        }
    }

    // 2. Filter to LEAF paragraphs only (no nested <a:p> inside)
    //    Parent <a:p> that contain table cells etc. must be skipped to avoid
    //    replacing text inside nested structures during export.
    const leafPositions = paraPositions.filter((pos, _idx, arr) => {
        // A paragraph is a "leaf" if no other paragraph is contained within it
        const isParent = arr.some(other =>
            other !== pos &&
            other.xmlStart > pos.xmlStart &&
            other.xmlEnd < pos.xmlEnd
        );
        return !isParent;
    });

    // 3. Extract text from each leaf paragraph using string positions
    for (const pos of leafPositions) {
        const paraXml = slideXml.substring(pos.xmlStart, pos.xmlEnd);

        // Extract all <a:t>text</a:t> within this paragraph
        // Regex matches ONLY <a:t> or <a:t ...>, NOT <a:tbl>, <a:tcPr>, etc.
        // Key: after "a:t" must be ">" or " " (space for attributes)
        const textPieces: string[] = [];
        const tRegex = /<a:t(?:\s[^>]*)?>([^<]*)<\/a:t>/g;
        let tMatch;
        while ((tMatch = tRegex.exec(paraXml)) !== null) {
            if (tMatch[1]) {
                // Decode XML entities
                const decoded = tMatch[1]
                    .replace(/&amp;/g, '&')
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&apos;/g, "'");
                textPieces.push(decoded);
            }
        }

        const paraText = textPieces.join('');

        if (paraText.trim()) {
            textLines.push(paraText);
            paragraphs.push({
                text: paraText,
                xmlStart: pos.xmlStart,
                xmlEnd: pos.xmlEnd,
            });
        }
    }

    return {
        text: textLines.join('\n'),
        paragraphs,
    };
}
